import {
  Box,
  Container,
  Divider,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  VStack,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import CardForm from "components/ui/CardForm";
import ColorPicker from "components/ui/ColorPicker/ColorPicker";
import CustomRadioGroup from "components/ui/CustomRadioGroup";
import FixedBar from "components/ui/FixedBar";
import FormControlGroup from "components/ui/FormControlGroup";
import SubmitButton from "components/ui/SubmitButton";
import { useSuccessDialogContext } from "components/ui/SuccessDialog/SuccessDialogContext";
import UploadAsset from "features/companies/components/UploadAsset";
import SimpleEditor from "features/editor/components/SimpleEditor/SimpleEditor";
import { useMessages } from "hooks/useMessages";
import { preventEnterKeySubmission } from "lib/validation";
import {
  FormProvider,
  Resolver,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { brandColorPalette } from "theme/theme";
import * as Yup from "yup";
import { useSaveContentGroup } from "../hooks/useSaveContentGroup";
import TemplateEmailPreview from "./TemplateEmailPreview";

export interface FormValues {
  type: string;
  name: string;
  logo: string;
  logoShare: string;
  color: string;
  colorTitle: string;
  colorHighlight: string;
  footerImage?: string;
  footerDescription?: string;
  guidelines?: string;
  footerLinkedInUrl?: string;
  footerSiteUrl?: string;
  footerEmail?: string;
  active?: boolean;
  emailTemplateId?: string;
  language: string;
}

interface ContentGroupFormProps {
  contentGroup?: any;
  type?: any;
}

const DEFAULT_COLORS = {
  primary: "#77A131",
  title: "#004A77",
};

const validationSchema = Yup.object().shape({
  type: Yup.string().required(),
  name: Yup.string().required(),
  logo: Yup.string().required(),
  logoShare: Yup.string().required(),
  color: Yup.string().required(),
  language: Yup.string().required(),
  colorTitle: Yup.string().required(),
  colorHighlight: Yup.string().required(),
});

const types = {
  REPORT: "Modelo de Report",
  AGENDA: "Modelo de Agenda",
  NEWSLETTER: "Modelo de Newsletter",
};

export default function ContentGroupForm({
  contentGroup,
  type,
}: ContentGroupFormProps) {
  const navigate = useNavigate();
  const { openSuccessDialog } = useSuccessDialogContext();

  const defaultValues: FormValues = {
    type: type ?? contentGroup?.type ?? "REPORT",
    name: contentGroup?.name,
    language: contentGroup?.language || "PT_BR",
    active: contentGroup?.active,
    logo: contentGroup?.emailTemplate?.logo,
    logoShare: contentGroup?.emailTemplate?.logoShare,
    color: contentGroup?.emailTemplate?.color ?? DEFAULT_COLORS.primary,
    colorTitle: contentGroup?.emailTemplate?.colorTitle ?? DEFAULT_COLORS.title,
    colorHighlight:
      contentGroup?.emailTemplate?.colorHighlight ?? DEFAULT_COLORS.primary,
    footerImage: contentGroup?.emailTemplate?.footerImage,
    footerDescription: contentGroup?.emailTemplate?.footerDescription,
    guidelines: contentGroup?.emailTemplate?.guidelines,
    footerLinkedInUrl: contentGroup?.emailTemplate?.footerLinkedInUrl,
    footerSiteUrl: contentGroup?.emailTemplate?.footerSiteUrl,
    footerEmail: contentGroup?.emailTemplate?.footerEmail,
    emailTemplateId: contentGroup?.emailTemplate?.id,
  };

  const methods = useForm<FormValues>({
    mode: "all",
    resolver: yupResolver(validationSchema) as Resolver<FormValues>,
    defaultValues,
  });

  const {
    handleSubmit,
    control,
    register,
    reset,
    watch,
    setValue,
    formState: { errors, isValid },
  } = methods;

  const isActive = watch("active");

  const { onError } = useMessages();

  const { onSaveContentGroup, isPending } = useSaveContentGroup({
    contentGroupId: contentGroup?.id,
    onSuccess: () => {
      reset();
      navigate("/modelos-de-disparo");
      if (!contentGroup) {
        openSuccessDialog({
          title: "Novo modelo cadastrado!",
          description: `Você já pode criar e enviar conteúdos usando esse modelo`,
          actionButtonText: "Criar mais um modelo",
          actionButton: "/modelos-de-disparo/novo",
          closeButtonText: "Tudo certo",
        });
      }
    },
    onError: (error) => onError(error?.response?.data?.message),
  });
  const onSubmit: SubmitHandler<FormValues> = (data) =>
    onSaveContentGroup(data);

  return (
    <Container variant="internal" flexDirection="column">
      <Heading as="h1" size="h1">
        Dados do modelo
        <span>Identifique um novo modelo de disparo/produto</span>
      </Heading>

      <CardForm mb="90px">
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            onKeyPress={preventEnterKeySubmission}
          >
            <HStack
              alignItems="flex-start"
              justifyContent="space-between"
              spacing="20px"
            >
              <Box w="full" maxW="587px">
                <FormControl>
                  <FormLabel>Tipo</FormLabel>
                  <Select disabled>
                    <option value={defaultValues.type}>
                      {types[defaultValues.type]}
                    </option>
                  </Select>
                </FormControl>

                <FormControl isInvalid={!!errors.language} isRequired>
                  <FormLabel>Idioma</FormLabel>
                  <CustomRadioGroup
                    name="language"
                    defaultValue={defaultValues.language}
                    onChange={(value) =>
                      setValue("language", value, { shouldValidate: true })
                    }
                    options={[
                      {
                        label: "Português",
                        value: "PT_BR",
                      },
                      {
                        label: "Inglês",
                        value: "EN",
                      },
                    ]}
                  />
                </FormControl>

                <FormControlGroup>
                  <FormControl isInvalid={!!errors.name} isRequired>
                    <FormLabel>Nome do modelo</FormLabel>
                    <Input {...register("name")} />
                  </FormControl>
                </FormControlGroup>

                <FormControlGroup>
                  <FormControl isInvalid={!!errors.logo} isRequired>
                    <FormLabel>Logo do modelo</FormLabel>
                    <Box mb="30px">
                      <UploadAsset
                        defaultValue={defaultValues.logo}
                        onChange={(url) =>
                          setValue("logo", url, { shouldValidate: true })
                        }
                      />
                      <small>Dimensões recomendadas: 160x50 pixels</small>
                    </Box>
                  </FormControl>
                </FormControlGroup>

                <FormControlGroup>
                  <FormControl isInvalid={!!errors.logoShare} isRequired>
                    <FormLabel>Logo para compartilhamento de link</FormLabel>
                    <Box mb="30px">
                      <UploadAsset
                        defaultValue={defaultValues.logoShare}
                        onChange={(url) =>
                          setValue("logoShare", url, { shouldValidate: true })
                        }
                      />
                      <small>Dimensões recomendadas: 600x600 pixels</small>
                    </Box>
                  </FormControl>
                </FormControlGroup>

                <SimpleGrid columns={3}>
                  <FormControl isInvalid={!!errors.color} isRequired>
                    <VStack align="flex-start">
                      <FormLabel>Cor tema do modelo</FormLabel>
                      <ColorPicker
                        colors={brandColorPalette}
                        defaultColor={defaultValues.color}
                        onColorChange={(color) =>
                          setValue("color", color.hex, { shouldValidate: true })
                        }
                      />
                    </VStack>
                  </FormControl>

                  <FormControl isInvalid={!!errors.colorTitle} isRequired>
                    <VStack align="flex-start">
                      <FormLabel>Cor do título</FormLabel>
                      <ColorPicker
                        colors={brandColorPalette}
                        defaultColor={defaultValues.colorTitle}
                        onColorChange={(color) =>
                          setValue("colorTitle", color.hex, {
                            shouldValidate: true,
                          })
                        }
                      />
                    </VStack>
                  </FormControl>

                  <FormControl isInvalid={!!errors.colorHighlight} isRequired>
                    <VStack align="flex-start">
                      <FormLabel>Cor dos destaques</FormLabel>
                      <ColorPicker
                        colors={brandColorPalette}
                        defaultColor={defaultValues.colorHighlight}
                        onColorChange={(color) =>
                          setValue("colorHighlight", color.hex, {
                            shouldValidate: true,
                          })
                        }
                      />
                    </VStack>
                  </FormControl>
                </SimpleGrid>

                <Divider my="24px" />

                <Heading as="h4" size="h4">
                  Rodapé
                  <span>
                    Os campos não preenchidos terão os links ocultados no e-mail
                  </span>
                </Heading>

                <Stack spacing="18px" direction="row" mt="20px">
                  <FormControl>
                    <FormLabel>Site</FormLabel>
                    <Input {...register("footerSiteUrl")} />
                  </FormControl>

                  <FormControl>
                    <FormLabel>LinkedIn</FormLabel>
                    <Input {...register("footerLinkedInUrl")} />
                  </FormControl>

                  <FormControl>
                    <FormLabel>E-mail</FormLabel>
                    <Input {...register("footerEmail")} />
                  </FormControl>
                </Stack>

                <FormControl>
                  <FormLabel>Endereço</FormLabel>
                  <SimpleEditor
                    name="footerDescription"
                    control={control}
                    skipExtensionConfigs
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Orientações de conteúdo</FormLabel>
                  <SimpleEditor name="guidelines" control={control} />
                </FormControl>

                <FormControl>
                  <FormLabel>Imagem rodapé</FormLabel>
                  <Box mb="30px">
                    <UploadAsset
                      defaultValue={defaultValues.footerImage}
                      onChange={(url) =>
                        setValue("footerImage", url, { shouldValidate: true })
                      }
                    />
                  </Box>
                </FormControl>

                {contentGroup && (
                  <FormControl isInvalid={!!errors.active}>
                    <FormLabel>Status</FormLabel>
                    <Switch {...register("active")}>
                      {isActive ? "Ativo" : "Inativo"}
                    </Switch>
                  </FormControl>
                )}
              </Box>

              <TemplateEmailPreview />
            </HStack>

            <FixedBar>
              <SubmitButton
                isDisabled={isPending || !isValid}
                isLoading={isPending}
                hideIcon
              >
                {!contentGroup ? "Cadastrar" : "Atualizar"}
              </SubmitButton>
            </FixedBar>
          </form>
        </FormProvider>
      </CardForm>
    </Container>
  );
}
